import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Users, Zap, Target, Settings, Headphones } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

function Reveal({ children, delay = 0, className = '' }) {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <div
      ref={ref}
      className={`${isVisible ? 'reveal-visible' : 'reveal-hidden'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ─── Why iMane Hero ─── */
function WhyHero() {
  return (
    <section className="bg-brand-navy py-20 lg:py-28" data-testid="why-imane-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <p className="text-brand-orange font-semibold text-sm uppercase tracking-widest mb-4 font-source-sans">
              Why iMANE
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-lexend font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight mb-6">
              The Boutique <span className="text-brand-orange">Advantage</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-source-sans">
              Why the world's most demanding procurement teams choose a specialist over a
              generalist.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─── 5 Boutique Pillars ─── */
function BoutiquePillars() {
  const pillars = [
    {
      icon: Users,
      title: 'Senior Expertise at Every Stage',
      body: 'Every engagement is led by practitioners with 10+ years of hands-on experience. You will never find a junior consultant managing your transformation while a senior takes credit. Our entire team operates at the level that most large firms reserve for their most senior partners.',
    },
    {
      icon: Zap,
      title: 'Accelerator-Led Delivery',
      body: 'Pre-built industry-specific templates, playbooks, and configuration accelerators compress timelines dramatically. Our clients go live in 4-8 months, not 18. We have done this enough times to know exactly where the complexity lies - and how to navigate it efficiently.',
    },
    {
      icon: Target,
      title: 'Business-Outcome Obsession',
      body: 'We measure success by savings realised, cycle times reduced, and maverick spend eliminated - not billable hours logged. Fixed outcome-based engagements align our incentives completely with yours. When you win, we win.',
    },
    {
      icon: Settings,
      title: 'Cloud & Platform Extensibility',
      body: 'Open integration, low-code extensions, and rapid innovation on cloud platforms means we can build custom procurement capabilities that no off-the-shelf deployment can match. Your business is not generic - your procurement environment should not be either.',
    },
    {
      icon: Headphones,
      title: 'Post-Go-Live Managed Services',
      body: 'We do not disappear at go-live. Our managed services offering provides continuous optimisation, system enhancement, user adoption support, and strategic guidance to maximise your procurement technology investment long after launch day.',
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="boutique-pillars">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-brand-navy tracking-tight mb-4">
              The 5 Boutique Pillars
            </h2>
          </Reveal>
        </div>
        <div className="space-y-6">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 70}>
              <div
                className="bg-brand-bg rounded-xl p-8 border border-brand-border hover:border-brand-orange transition-colors duration-200"
                data-testid={`pillar-${i + 1}`}
              >
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-xl bg-brand-orange-light flex items-center justify-center flex-shrink-0">
                    <p.icon size={24} className="text-brand-orange" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-jetbrains text-brand-orange text-sm font-bold">
                        0{i + 1}
                      </span>
                      <h3 className="font-lexend font-semibold text-xl text-brand-navy">
                        {p.title}
                      </h3>
                    </div>
                    <p className="text-slate-600 text-base leading-relaxed font-source-sans max-w-3xl">
                      {p.body}
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Team Image ─── */
function TeamImage() {
  return (
    <section className="py-10 bg-white" data-testid="why-team-image">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-xl overflow-hidden shadow-sm border border-brand-border" style={{ borderRadius: '12px' }}>
          <img
            src="/images/team-table.jpg"
            alt="Senior procurement specialists in client engagement"
            loading="lazy"
            className="w-full h-64 lg:h-80 object-cover"
          />
        </div>
      </div>
    </section>
  );
}

/* ─── What Sets iMANE Apart (iMANE USPs only) ─── */
function ComparisonTable() {
  const rows = [
    { dimension: 'Focus', imane: 'Deep solution and domain niche - Supply Chain & Procurement only' },
    { dimension: 'Delivery speed', imane: '4-8 month rapid deployments' },
    { dimension: 'Team model', imane: 'Senior-led throughout every engagement' },
    { dimension: 'Engagement style', imane: 'Partner-to-partner collaboration' },
    { dimension: 'Value model', imane: 'Fixed outcome-based pricing' },
    { dimension: 'IP & accelerators', imane: 'Industry-specific templates & tools' },
  ];

  return (
    <section className="py-20 lg:py-28 bg-brand-orange" data-testid="why-comparison-table">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-brand-navy tracking-tight mb-4">
              What Sets iMANE Apart
            </h2>
          </Reveal>
          <Reveal delay={60}>
            <p className="text-slate-800 text-base font-source-sans leading-relaxed">
              Six core differentiators that make iMANE the preferred choice for
              ambitious organisations transforming their procurement function.
            </p>
          </Reveal>
        </div>
        <Reveal delay={80}>
          <div className="overflow-x-auto rounded-xl border border-brand-navy/20 shadow-sm">
            <table className="w-full text-sm bg-white" data-testid="why-comparison-table-element">
              <thead>
                <tr className="bg-white border-b border-brand-border">
                  <th className="text-left px-6 py-4 font-lexend font-semibold text-slate-500 text-xs uppercase tracking-wider w-1/3">
                    Dimension
                  </th>
                  <th className="text-left px-6 py-4 font-lexend font-semibold text-brand-orange text-xs uppercase tracking-wider">
                    iMANE Delivers
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {rows.map((row) => (
                  <tr
                    key={row.dimension}
                    className="group hover:bg-orange-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 font-medium text-slate-700 font-source-sans">
                      {row.dimension}
                    </td>
                    <td className="px-6 py-4 font-semibold text-slate-900 font-source-sans">
                      {row.imane}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Industries We Serve ─── */
function IndustriesServed() {
  const industries = [
    'Manufacturing', 'Retail', 'Real Estate', 'Energy', 'Healthcare & Pharma',
    'Public Sector', 'Amusement Parks', 'Education', 'FMCG', 'Service & IT Infra',
    'Process Industry', 'Tobacco',
  ];

  return (
    <section className="py-16 lg:py-20 bg-brand-navy border-y border-slate-700" data-testid="industries-served">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <Reveal>
            <h2 className="font-lexend font-bold text-2xl lg:text-3xl text-white tracking-tight">
              Industries We Serve
            </h2>
          </Reveal>
        </div>
        <Reveal delay={80}>
          <div className="flex flex-wrap gap-3 justify-center" data-testid="industry-tags">
            {industries.map((ind) => (
              <span
                key={ind}
                className="px-5 py-2.5 bg-brand-bg border border-brand-border rounded-full text-sm font-medium text-slate-700 hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange-light transition-all duration-200 cursor-default font-source-sans"
              >
                {ind}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Why iMane CTA ─── */
function WhyCTA() {
  return (
    <section className="py-20 bg-brand-bg" data-testid="why-cta">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Reveal>
          <h2 className="font-lexend font-bold text-3xl text-brand-navy mb-5">
            Ready to experience the boutique advantage?
          </h2>
        </Reveal>
        <Reveal delay={80}>
          <p className="text-slate-600 font-source-sans mb-8 max-w-xl mx-auto">
            Talk to a senior specialist and discover how iMANE delivers
            outcomes that large firms simply cannot match.
          </p>
        </Reveal>
        <Reveal delay={160}>
          <div className="flex flex-col items-center">
            <a
              href="https://wa.me/919637625311"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-orange"
              data-testid="why-cta-btn"
            >
              Talk to us <ArrowRight size={16} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function WhyImane() {
  return (
    <div data-testid="why-imane-page">
      <Helmet>
        <title>Why Choose iMANE? | Boutique Procurement Specialists | Mane Labs LLP</title>
        <meta name="description" content="iMANE delivers faster, more outcome-focused procurement transformations than large consulting firms. Senior-led, fixed outcome-based pricing, 4-8 month deployments." />
        <meta name="keywords" content="why iMANE, iMANE vs big consulting, boutique procurement consulting, best SAP Ariba consultant India, mane labs why choose, iMANE specialists" />
        <link rel="canonical" href="https://www.imane.in/why-imane" />
        <meta property="og:title" content="Why Choose iMANE? | Boutique Procurement Specialists | Mane Labs LLP" />
        <meta property="og:description" content="Senior-led, outcome-focused procurement transformations. Fixed pricing. 4-8 month deployments. Boutique beats big." />
        <meta property="og:url" content="https://www.imane.in/why-imane" />
      </Helmet>
      <WhyHero />
      <BoutiquePillars />
      <TeamImage />
      <ComparisonTable />
      <IndustriesServed />
      <WhyCTA />
    </div>
  );
}
